/**
 * Okuma profilleri — kullanıcı NE yüklediğini söyler, okuma ona göre daralır.
 *
 * Aynı PDF bir satış faturası da olabilir, bir alış faturası da. Metinden
 * yönü (gelir mi gider mi) tahmin etmek, defterin en sık bozulduğu yerdi.
 * Kullanıcının bir kez tıklaması bu tahmini ortadan kaldırır.
 *
 * Profil üç şeyi sabitler:
 *   1. YÖN      — satış faturası gelirdir, alış faturası ve fiş giderdir.
 *                 Ekstrede yön satırın kendisindedir; zorlanmaz.
 *   2. KONTROL  — fatura ve fişte ilan edilen toplam; ekstrede
 *                 açılış + hareketler = kapanış.
 *   3. AI İPUCU — AI yalnızca son kademede çağrılır; ona da dar bir görev
 *                 verilir, "bu belgede ne var" diye serbest soru sorulmaz.
 *
 * Arayüz dili İngilizce; etiketler burada DEĞİL, ekrandadır. Bu dosya
 * yalnızca davranışı taşır.
 */

import { EPSILON, type TotalsCheck } from './validate.js';

export const DOC_KINDS = [
  'invoice_sales', 'invoice_purchase', 'receipt', 'bank_statement', 'other',
] as const;
export type DocKind = typeof DOC_KINDS[number];

export type TotalCheckMode = 'declared' | 'balance' | 'none';

export interface ReadingProfile {
  readonly kind: DocKind;
  /** Satırlara zorlanacak yön. null = satırın kendi yönü korunur. */
  readonly direction: 'income' | 'expense' | null;
  /** Hangi aritmetik kontrol çalışır. */
  readonly totalCheck: TotalCheckMode;
  /** AI'ya eklenecek dar görev tanımı. 'other' için boş. */
  readonly aiHint: string;
}

const PROFILES: Record<DocKind, ReadingProfile> = {
  invoice_sales: {
    kind: 'invoice_sales',
    direction: 'income',
    totalCheck: 'declared',
    aiHint: 'This is a SALES invoice the user issued to a customer, so every row is income. '
      + 'Return the invoice line items; if the lines are not itemised, return one row with the grand total. '
      + 'Do not return subtotal, tax or total rows as separate items.',
  },
  invoice_purchase: {
    kind: 'invoice_purchase',
    direction: 'expense',
    totalCheck: 'declared',
    aiHint: 'This is a PURCHASE invoice the user received from a supplier, so every row is expense. '
      + 'Return the invoice line items; if the lines are not itemised, return one row with the grand total. '
      + 'Do not return subtotal, tax or total rows as separate items.',
  },
  receipt: {
    kind: 'receipt',
    direction: 'expense',
    totalCheck: 'declared',
    aiHint: 'This is a receipt for something the user paid for. '
      + 'Return ONE row: the total paid as the amount, the merchant name as the note, and the receipt date.',
  },
  bank_statement: {
    kind: 'bank_statement',
    direction: null,
    totalCheck: 'balance',
    aiHint: 'This is a bank or card statement. Return every transaction row: money in is income, money out is expense. '
      + 'Do not return opening, closing or running balance rows.',
  },
  other: {
    kind: 'other',
    direction: null,
    totalCheck: 'declared',
    aiHint: '',
  },
};

export function isDocKind(v: unknown): v is DocKind {
  return typeof v === 'string' && (DOC_KINDS as readonly string[]).includes(v);
}

/** Bilinmeyen değer 'other' olur: yanlış bir profili zorlamaktan güvenlidir. */
export function profileFor(kind: unknown): ReadingProfile {
  return PROFILES[isDocKind(kind) ? kind : 'other'];
}

type Row = { type?: unknown; amount?: unknown };

/**
 * Belgenin "ana yönü": okumanın verdiği yönlerden TUTARCA büyük olanı.
 *
 * Okuma yönü işaretten gelir ve belge türünü bilmez: pozitif tutarlı bir alış
 * faturası "gelir" okunabilir. Önemli olan hangi yönün ana satırlar olduğu,
 * hangisinin indirim/iade olduğudur. İndirim neredeyse her zaman ana
 * kalemlerden küçüktür; bu yüzden SAYI değil TUTAR karşılaştırılır.
 * Eşitlikte ana yön bilinemez: null.
 */
function mainType(rows: readonly Row[]): 'income' | 'expense' | 'all' | null {
  let inc = 0, exp = 0;
  for (const r of rows) {
    const a = Math.abs(Number(r.amount));
    const v = Number.isFinite(a) ? a : 0;
    if (r.type === 'income') inc += v;
    else if (r.type === 'expense') exp += v;
  }
  const hasInc = rows.some((r) => r.type === 'income');
  const hasExp = rows.some((r) => r.type === 'expense');
  if (!hasInc || !hasExp) return 'all';
  if (Math.abs(inc - exp) <= EPSILON) return null;
  return inc > exp ? 'income' : 'expense';
}

/**
 * Belgenin geri kalanına TERS yönde okunmuş satırlar (indis listesi).
 *
 * Satış faturasında "-50 indirim" gibi. Bunlar sessizce ana yöne çevrilmez:
 * ters yönde kaydedilir (net toplam doğru kalsın) ve kullanıcıya sorulur.
 * Ana yön bilinemiyorsa (eşit tutarlar) belgenin BÜTÜN satırları sorulur.
 */
export function againstTheGrain(rows: readonly Row[], kind: unknown): number[] {
  if (!profileFor(kind).direction) return [];
  const main = mainType(rows);
  if (main === 'all') return [];
  const out: number[] = [];
  rows.forEach((r, i) => {
    if (main === null) out.push(i);
    else if ((r.type === 'income' || r.type === 'expense') && r.type !== main) out.push(i);
  });
  return out;
}

/**
 * Profilin yönünü satırlara uygula. Yeni dizi döner; girdi değişmez.
 *
 * Yön yalnızca kullanıcı belge türünü SÖYLEDİĞİNDE uygulanır. Ekstre ve
 * "diğer" belgede her satırın yönü okumadan gelir ve dokunulmaz.
 *
 * Ana satırlar profilin yönünü alır; ters işaretli satırlar (indirim, iade)
 * ters yönü alır. Defterde "eksi gelir" yoktur: satış faturasındaki 50'lik
 * indirimi gelir yazmak toplamı 100 şaşırtır, gider yazmak net toplamı korur.
 * Ana yön bilinemiyorsa hepsi profilin yönünü alır ve hepsi sorulur
 * (bkz. againstTheGrain).
 */
export function applyDirection<T extends Row>(
  rows: readonly T[],
  kind: unknown,
): T[] {
  const dir = profileFor(kind).direction;
  if (!dir) return rows.slice();
  const other = dir === 'income' ? 'expense' : 'income';
  const main = mainType(rows);
  return rows.map((r) => {
    const opposite = main !== 'all' && main !== null
      && (r.type === 'income' || r.type === 'expense') && r.type !== main;
    return { ...r, type: opposite ? other : dir };
  });
}

/* ── Ekstre: açılış + hareketler = kapanış ─────────────────────────── */

/* Genel "balance" / "bakiye" kelimesi BİLEREK yok: her satırda yürüyen
   bakiye sütunu olan ekstrelerde her satır eşleşir ve kontrol anlamsızlaşır.
   Yalnızca açılışı ve kapanışı açıkça söyleyen etiketler kabul edilir. */
const OPENING =
  /(opening\s*balance|previous\s*balance|balance\s*brought\s*forward|starting\s*balance|beginning\s*balance|anfangssaldo|alter\s*(?:kontostand|saldo)|saldovortrag|devreden\s*bakiye|a[çc][ıi]l[ıi][şs]\s*bakiyesi|[öo]nceki\s*bakiye|solde\s*(?:initial|pr[ée]c[ée]dent|d'ouverture)|ancien\s*solde|saldo\s*(?:inicial|anterior))/i;
const CLOSING =
  /(closing\s*balance|new\s*balance|ending\s*balance|final\s*balance|balance\s*carried\s*forward|endsaldo|neuer\s*(?:kontostand|saldo)|kapan[ıi][şs]\s*bakiyesi|son\s*bakiye|solde\s*final|nouveau\s*solde|saldo\s*final)/i;

function numberAfter(
  line: string,
  label: RegExp,
  stop: RegExp,
  parseValue: (raw: string) => number | null,
): number | null {
  const at = line.search(label);
  if (at < 0) return null;
  let after = line.slice(at).replace(label, '');
  /* Aynı satırda öbür etiket varsa onun sayısını alma:
     "Opening balance 100.00  Closing balance 250.00" */
  const cut = after.search(stop);
  if (cut >= 0) after = after.slice(0, cut);
  /* Tarihler tutar sanılmasın: "Closing balance 31.03.2026 5.400,00" */
  after = after.replace(/\b\d{1,4}[./-]\d{1,2}[./-]\d{2,4}\b/g, ' ');
  /* Eksi işareti rakama BİTİŞİK olmalı; "balance - 1,234.56" ayraçtır. */
  const m = after.match(/-?\d[\d.,\s']*\d-?|-?\d-?/g);
  if (!m || !m.length) return null;
  let raw = m[m.length - 1].replace(/\s+/g, '');
  /* Bazı bankalar negatifi sonda yazar: "1.234,56-" */
  let neg = false;
  if (raw.endsWith('-')) { neg = true; raw = raw.slice(0, -1); }
  if (raw.startsWith('-')) { neg = true; raw = raw.slice(1); }
  const v = parseValue(raw);
  if (v === null || !Number.isFinite(v)) return null;
  return neg ? -Math.abs(v) : v;
}

export interface StatementBalances {
  readonly opening: number | null;
  readonly closing: number | null;
}

/**
 * Metinde açılış ve kapanış bakiyesini ara.
 *
 * Açılış için İLK, kapanış için SON eşleşme alınır: çok sayfalı ekstrelerde
 * her sayfa başında "devreden" tekrar edebilir. Bulunamayan değer null'dır ve
 * kontrol çalışmaz. Yanlış bir bakiyeyle sahte uyarı üretmek, hiç uyarmamaktan
 * kötüdür.
 */
export function findStatementBalances(
  lines: readonly string[],
  parseValue: (raw: string) => number | null,
): StatementBalances {
  let opening: number | null = null;
  let closing: number | null = null;
  for (const raw of lines) {
    const line = String(raw ?? '');
    if (opening === null && OPENING.test(line)) opening = numberAfter(line, OPENING, CLOSING, parseValue);
    if (CLOSING.test(line)) {
      const v = numberAfter(line, CLOSING, OPENING, parseValue);
      if (v !== null) closing = v;
    }
  }
  return { opening, closing };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Hareketler açılıştan kapanışa götürüyor mu?
 *
 * İKİ İŞARET KURALI da kabul edilir. Banka hesabında gelir bakiyeyi artırır;
 * kredi kartı ekstresinde ise harcama "borç bakiyesini" artırır. Hangisi
 * olduğunu belgeden güvenle çıkaramayız ve yanlış kuralla her kart ekstresine
 * sahte uyarı basardık. Kaçan satırı yakalayan şey farkın BÜYÜKLÜĞÜDÜR; o iki
 * kuralda da aynı şekilde yakalanır.
 */
export function checkStatementBalance(
  opening: number | null,
  closing: number | null,
  rows: readonly { amount: unknown; type: unknown }[],
): TotalsCheck {
  const OK: TotalsCheck = { ok: true, difference: 0, reason: '' };
  if (opening === null || closing === null) return OK;
  if (!Number.isFinite(opening) || !Number.isFinite(closing)) return OK;

  let net = 0;
  for (const r of rows) {
    const a = Math.abs(Number(r.amount));
    if (!Number.isFinite(a)) continue;
    net += r.type === 'income' ? a : -a;
  }
  net = round2(net);
  const expected = round2(closing - opening);

  const bank = round2(net - expected);
  const card = round2(-net - expected);
  if (Math.abs(bank) <= EPSILON || Math.abs(card) <= EPSILON) return OK;

  const diff = Math.abs(bank) <= Math.abs(card) ? bank : card;
  return {
    ok: false,
    difference: diff,
    reason:
      `The statement opens at ${opening.toFixed(2)} and closes at ${closing.toFixed(2)}, `
      + `a change of ${expected.toFixed(2)}, but the transactions read add up to ${net.toFixed(2)}. `
      + 'A transaction may have been missed or read twice - please check.',
  };
}
